/**
 * useStrategyDNA Hook
 *
 * Manages Strategy DNA data fetching, caching, and state management.
 * Provides access to bounce/break/fakeout statistics and fakeout warnings.
 *
 * Backend Field Reference:
 * - avgMfePips, avgMaePips: Values in pips
 * - time_to_outcome_mins: Inside outcomeDetails JSON (integer, minutes)
 * - totalEvents: Event count
 * - bounceRate, breakRate, fakeoutRate: Percentages (e.g., 72.3)
 * - p75FakeoutDepth, p95FakeoutDepth: Fakeout depth in pips
 *
 * @see src/api/strategyDnaApi.js for API functions
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
    fetchAllDNAData,
    getLevelStats,
    getDNAEvents,
    getFakeoutWarnings
} from '../api/strategyDnaApi';

/**
 * Custom hook for Strategy DNA data management
 *
 * @param {Object} options - Hook configuration
 * @param {string} options.symbol - Trading symbol (e.g., 'XAUUSD_ct')
 * @param {string} options.token - JWT authentication token
 * @param {boolean} options.autoRefresh - Enable auto-refresh (default: true)
 * @param {number} options.refreshInterval - Refresh interval in ms (default: 60000)
 * @param {boolean} options.enabled - Enable data fetching (default: true)
 * @returns {Object} Data, state, and actions
 */
export const useStrategyDNA = ({
    symbol,
    token,
    autoRefresh = true,
    refreshInterval = 60000, // 1 minute default
    enabled = true
}) => {
    // Data state
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetchedAt, setLastFetchedAt] = useState(null);
    const [fetchCount, setFetchCount] = useState(0);

    // Filter state for level stats
    const [activeFilters, setActiveFilters] = useState({});

    // Ref to track if initial fetch has happened
    const initialFetchDone = useRef(false);

    /**
     * Main data fetch function
     */
    const fetchData = useCallback(async (isManualRefresh = false) => {
        if (!token || !symbol || !enabled) {
            return;
        }

        // Set appropriate loading state
        if (isManualRefresh || fetchCount > 0) {
            setIsRefreshing(true);
        } else {
            setLoading(true);
        }
        setError(null);

        try {
            const result = await fetchAllDNAData(token, symbol);
            setData(result);
            setLastFetchedAt(new Date());
            setFetchCount(prev => prev + 1);

            console.log('[useStrategyDNA] Data fetched successfully:', {
                symbol,
                statsCount: result.levelStats?.stats?.length || 0,
                warningsCount: result.levelStats?.highFakeoutWarnings?.length || 0
            });
        } catch (err) {
            console.error('[useStrategyDNA] Fetch failed:', err);
            setError(err.message || 'Failed to fetch Strategy DNA data');
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [token, symbol, enabled, fetchCount]);

    /**
     * Initial data fetch
     */
    useEffect(() => {
        if (!initialFetchDone.current && token && symbol && enabled) {
            fetchData();
            initialFetchDone.current = true;
        }
    }, [fetchData, token, symbol, enabled]);

    /**
     * Auto-refresh interval
     */
    useEffect(() => {
        if (!autoRefresh || !enabled || !token || !symbol) {
            return;
        }

        const interval = setInterval(() => {
            fetchData(true);
        }, refreshInterval);

        return () => clearInterval(interval);
    }, [fetchData, autoRefresh, refreshInterval, enabled, token, symbol]);

    /**
     * Reset when symbol changes
     */
    useEffect(() => {
        initialFetchDone.current = false;
        setData(null);
        setError(null);
        setFetchCount(0);
        setActiveFilters({});
    }, [symbol]);

    /**
     * Fetch filtered level stats
     * Returns fresh data without updating main state
     */
    const fetchFilteredLevelStats = useCallback(async (filters) => {
        if (!token || !symbol) {
            return null;
        }

        try {
            const result = await getLevelStats(token, symbol, filters);
            setActiveFilters(filters);
            return result;
        } catch (err) {
            console.error('[useStrategyDNA] Filtered fetch failed:', err);
            throw err;
        }
    }, [token, symbol]);

    /**
     * Fetch event details for drill-down
     */
    const fetchEvents = useCallback(async (filters = {}) => {
        if (!token || !symbol) {
            return null;
        }

        try {
            return await getDNAEvents(token, symbol, filters);
        } catch (err) {
            console.error('[useStrategyDNA] Events fetch failed:', err);
            throw err;
        }
    }, [token, symbol]);

    /**
     * Fetch fakeout warnings only
     */
    const fetchFakeoutWarnings = useCallback(async (minEvents = 3) => {
        if (!token || !symbol) {
            return [];
        }

        try {
            return await getFakeoutWarnings(token, symbol, minEvents);
        } catch (err) {
            console.error('[useStrategyDNA] Fakeout warnings fetch failed:', err);
            return [];
        }
    }, [token, symbol]);

    /**
     * Manual refresh
     */
    const refresh = useCallback(() => {
        fetchData(true);
    }, [fetchData]);

    /**
     * Clear all data
     */
    const clear = useCallback(() => {
        setData(null);
        setError(null);
        setLastFetchedAt(null);
        setFetchCount(0);
        setActiveFilters({});
        initialFetchDone.current = false;
    }, []);

    // Computed values from data
    const summary = data?.summary || null;
    const levelStats = data?.levelStats || null;
    const stats = levelStats?.stats || [];
    const topBounceLevels = levelStats?.topBounceLevels || [];
    const highFakeoutWarnings = levelStats?.highFakeoutWarnings || [];

    // Summary statistics (with safe fallbacks)
    const overallStats = summary ? {
        totalEvents: summary.totalEvents || 0,
        bounceRate: summary.bounceRate || 0,
        breakRate: summary.breakRate || 0,
        fakeoutRate: summary.fakeoutRate || 0,
        avgMfePips: summary.avgMfePips || 0,
        avgMaePips: summary.avgMaePips || 0
    } : null;

    return {
        // Raw data
        data,
        summary,
        levelStats,

        // Computed arrays
        stats,
        topBounceLevels,
        highFakeoutWarnings,

        // Summary stats
        overallStats,

        // State flags
        loading,
        isRefreshing,
        isLoading: loading || isRefreshing,
        hasData: !!data,
        hasError: !!error,
        error,

        // Metadata
        lastFetchedAt,
        fetchCount,
        activeFilters,

        // Actions
        refresh,
        clear,
        fetchFilteredLevelStats,
        fetchEvents,
        fetchFakeoutWarnings,
        setActiveFilters
    };
};

export default useStrategyDNA;
